/**
 * frontend/src/pages/valuation/_companyProfile.ts
 *
 * VAL-COMPANY-PROFILE-1B — form state <-> server payload for the Company
 * step. Form state is all strings (what the inputs hold); the payload is what
 * PATCH sends. Blank strings and blank rows are dropped and a profile with
 * nothing left is `null` — the server stores that as a cleared profile.
 */
import type {
  CompanyProfile,
  EngagementWithCompany,
  TransactionStructure,
} from '@/types/valuationCompany'

export interface PctRow { name: string; pct: string }
export interface MixRow { line: string; pct: string }
export type PersonRow = { first: string; last: string; role: string; contact: string }

/** Stored as one `name`: split at the last space, so "Mary Ann Lee" is first "Mary Ann", last "Lee". */
export function splitName(name: string): { first: string; last: string } {
  const t = name.trim()
  const i = t.lastIndexOf(' ')
  return i < 0 ? { first: t, last: '' } : { first: t.slice(0, i).trim(), last: t.slice(i + 1) }
}

export function joinName(first: string, last: string): string {
  return `${first.trim()} ${last.trim()}`.trim()
}

export const BLANK_PERSON: PersonRow = { first: '', last: '', role: '', contact: '' }

export interface CompanyState {
  business_description: string
  history: string
  restrictions_agreements: string
  facilities: string
  prior_sales: string
  geography: string
  assets_liabilities_included: string
  legal_entity_type: string
  transaction_structure: '' | TransactionStructure
  revenue_mix: MixRow[]
  owners: PctRow[]
  key_people: PersonRow[]
}

export const BLANK_COMPANY: CompanyState = {
  business_description: '',
  history: '',
  restrictions_agreements: '',
  facilities: '',
  prior_sales: '',
  geography: '',
  assets_liabilities_included: '',
  legal_entity_type: '',
  transaction_structure: '',
  revenue_mix: [],
  owners: [],
  key_people: [],
}

const TEXT_KEYS = [
  'business_description',
  'history',
  'restrictions_agreements',
  'facilities',
  'prior_sales',
  'geography',
  'assets_liabilities_included',
  'legal_entity_type',
] as const

export function companyFromEngagement(en: EngagementWithCompany): CompanyState {
  const p = en.company_profile
  if (!p) return { ...BLANK_COMPANY }
  return {
    business_description: p.business_description ?? '',
    history: p.history ?? '',
    restrictions_agreements: p.restrictions_agreements ?? '',
    facilities: p.facilities ?? '',
    prior_sales: p.prior_sales ?? '',
    geography: p.geography ?? '',
    assets_liabilities_included: p.assets_liabilities_included ?? '',
    legal_entity_type: p.legal_entity_type ?? '',
    transaction_structure: p.transaction_structure ?? '',
    revenue_mix: (p.revenue_mix ?? []).map(r => ({ line: r.line, pct: String(r.pct) })),
    owners: (p.owners ?? []).map(o => ({ name: o.name, pct: String(o.ownership_pct) })),
    key_people: (p.key_people ?? []).map(k => ({ ...splitName(k.name), role: k.role, contact: k.contact ?? '' })),
  }
}

// A blank % reads as 0 for the running total only. Save never sends one for a
// named row: missingPctMessage blocks it first, so a blank is never a silent 0.
function pct(v: string): number {
  const n = Number(v.trim() === '' ? 0 : v)
  return Number.isFinite(n) ? n : 0
}

export function companyToPayload(s: CompanyState): CompanyProfile | null {
  const out: CompanyProfile = {}
  for (const k of TEXT_KEYS) {
    const v = s[k].trim()
    if (v) out[k] = v
  }
  if (s.transaction_structure) out.transaction_structure = s.transaction_structure

  const mix = s.revenue_mix
    .filter(r => r.line.trim() !== '' || r.pct.trim() !== '')
    .map(r => ({ line: r.line.trim(), pct: pct(r.pct) }))
  if (mix.length) out.revenue_mix = mix

  const owners = s.owners
    .filter(o => o.name.trim() !== '' || o.pct.trim() !== '')
    .map(o => ({ name: o.name.trim(), ownership_pct: pct(o.pct) }))
  if (owners.length) out.owners = owners

  const people = s.key_people
    .filter(k => k.first.trim() !== '' || k.last.trim() !== '' || k.role.trim() !== '' || k.contact.trim() !== '')
    .map(k => {
      const row: { name: string; role: string; contact?: string } = { name: joinName(k.first, k.last), role: k.role.trim() }
      if (k.contact.trim()) row.contact = k.contact.trim()
      return row
    })
  if (people.length) out.key_people = people

  return Object.keys(out).length ? out : null
}

/**
 * One message per row: a row that has a name but no % reads 'Add the ownership %
 * for Owner A.' (`what` is 'ownership' or 'revenue'); every other row is null.
 */
export function missingPctMessages(
  what: 'ownership' | 'revenue',
  rows: Array<{ name: string; pct: string }>,
): Array<string | null> {
  return rows.map(r => (r.name.trim() !== '' && r.pct.trim() === '' ? `Add the ${what} % for ${r.name.trim()}.` : null))
}

/**
 * One message per key-people row: a row that is started but lacks a first name,
 * last name or role reads 'Key person 2: add a first name, last name and role.';
 * every other row is null. Phone or email is optional. A blank row is ignored.
 */
export function incompletePersonMessages(rows: PersonRow[]): Array<string | null> {
  return rows.map((k, i) =>
    (k.first.trim() !== '' || k.last.trim() !== '' || k.role.trim() !== '' || k.contact.trim() !== '')
    && (k.first.trim() === '' || k.last.trim() === '' || k.role.trim() === '')
      ? `Key person ${i + 1}: add a first name, last name and role.`
      : null)
}

/** Count of failing rows across the three row lists. */
export function failingRowCount(...lists: Array<Array<string | null>>): number {
  return lists.reduce((n, l) => n + l.filter(m => m !== null).length, 0)
}

/** The bottom-of-form line shown when Save is blocked. */
export function saveBlockedMessage(n: number): string {
  return `We couldn't save yet. Please fix the ${n} ${n === 1 ? 'item' : 'items'} marked in red above.`
}

/** Sum of a % column, rounded to 2 places so 0.1 + 0.2 prints as 0.3. */
export function pctTotal(rows: Array<{ pct: string }>): number {
  const sum = rows.reduce((t, r) => t + pct(r.pct), 0)
  return Math.round(sum * 100) / 100
}

export function isDirty(a: CompanyState, b: CompanyState): boolean {
  return JSON.stringify(a) !== JSON.stringify(b)
}
